import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  createSubgroup,
  deletePatient,
  deleteSubgroup,
  updateSubgroup,
} from "api/visaVitaService";

export const PATIENT_GROUPS = [
  { token: 'VISAVITA_GLUCOSE', label: 'Glucose', color: 'blue' },
  { token: 'VISAVITA_STRESS', label: 'Stress', color: 'red' },
  { token: 'VISAVITA_CORSANO', label: 'Corsano', color: 'green' },
];

export const groupLabel = (token) => PATIENT_GROUPS.find(group => group.token === token)?.label || token;

/**
 * One subgroup row: shows name and description, or an inline form to rename it, and a two-step delete.
 */
const SubgroupRow = ({ subgroup, onChanged }) => {
  const toast = useToast();
  const rowBg = useColorModeValue("gray.50", "gray.700");
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(subgroup.name);
  const [description, setDescription] = useState(subgroup.description || '');

  const save = async () => {
    setIsSaving(true);
    try {
      await updateSubgroup(subgroup.id, { name, description });
      setIsEditing(false);
      await onChanged();
    } catch (error) {
      toast({ title: "Could not rename subgroup", description: error.message, status: "error", isClosable: true });
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    setIsSaving(true);
    try {
      await deleteSubgroup(subgroup.id);
      toast({
        title: "Subgroup deleted",
        description: `${subgroup.patientCount} patient(s) stay in the group without a subgroup`,
        status: "success",
        isClosable: true,
      });
      await onChanged();
    } catch (error) {
      toast({ title: "Could not delete subgroup", description: error.message, status: "error", isClosable: true });
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <VStack align="stretch" spacing={2} p={3} bg={rowBg} borderRadius="md">
        <Input size="sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <Textarea size="sm" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" />
        <HStack justify="flex-end">
          <Button size="xs" variant="ghost" onClick={() => setIsEditing(false)}>Cancel</Button>
          <Button size="xs" colorScheme="purple" onClick={save} isLoading={isSaving} isDisabled={!name.trim()}>Save</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <HStack justify="space-between" p={3} bg={rowBg} borderRadius="md" align="start">
      <VStack align="start" spacing={0}>
        <HStack>
          <Text fontWeight="semibold" fontSize="sm">{subgroup.name}</Text>
          <Badge colorScheme="purple" variant="subtle">
            {subgroup.patientCount} {subgroup.patientCount === 1 ? 'patient' : 'patients'}
          </Badge>
        </HStack>
        {subgroup.description && <Text fontSize="xs" color="gray.500">{subgroup.description}</Text>}
      </VStack>
      {isConfirmingDelete ? (
        <HStack>
          <Text fontSize="xs" color="red.500">Delete subgroup?</Text>
          <Button size="xs" variant="ghost" onClick={() => setIsConfirmingDelete(false)}>No</Button>
          <Button size="xs" colorScheme="red" onClick={remove} isLoading={isSaving}>Delete</Button>
        </HStack>
      ) : (
        <HStack>
          <Button size="xs" variant="ghost" onClick={() => setIsEditing(true)}>Rename</Button>
          <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setIsConfirmingDelete(true)}>Delete</Button>
        </HStack>
      )}
    </HStack>
  );
};

/**
 * Create, rename and delete the subgroups of one patient group.
 */
export const SubgroupManagerModal = ({ isOpen, onClose, group, subgroups, onChanged }) => {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const groupSubgroups = subgroups.filter(subgroup => subgroup.group === group);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
    }
  }, [isOpen]);

  const create = async () => {
    setIsCreating(true);
    try {
      await createSubgroup(group, name.trim(), description.trim());
      toast({ title: "Subgroup created", description: name.trim(), status: "success", isClosable: true });
      setName('');
      setDescription('');
      await onChanged();
    } catch (error) {
      toast({ title: "Could not create subgroup", description: error.message, status: "error", isClosable: true });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{groupLabel(group)} subgroups</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.600">
              Subgroups split a patient group, for example by study. A patient belongs to at most one subgroup;
              deleting a subgroup keeps its patients in the {groupLabel(group)} group.
            </Text>
            {groupSubgroups.length === 0 ? (
              <Text fontSize="sm" color="gray.400">No subgroups yet.</Text>
            ) : (
              groupSubgroups.map(subgroup => (
                <SubgroupRow key={`${subgroup.id}-${subgroup.name}`} subgroup={subgroup} onChanged={onChanged} />
              ))
            )}
            <Divider />
            <FormControl>
              <FormLabel fontSize="sm">New subgroup</FormLabel>
              <VStack spacing={2} align="stretch">
                <Input size="sm" placeholder="Name, e.g. Study A" value={name} onChange={(e) => setName(e.target.value)} />
                <Textarea size="sm" rows={2} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
              </VStack>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Close</Button>
          <Button colorScheme="purple" onClick={create} isLoading={isCreating} isDisabled={!name.trim()}>
            Create subgroup
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/**
 * Confirmation for permanently deleting a patient. The patient ID has to be typed to enable the button.
 */
export const DeletePatientDialog = ({ patient, onClose, onDeleted }) => {
  const toast = useToast();
  const cancelRef = useRef();
  const warningBg = useColorModeValue("red.50", "red.900");
  const [confirmation, setConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setConfirmation('');
  }, [patient]);

  const remove = async () => {
    setIsDeleting(true);
    try {
      const result = await deletePatient(patient.patientId);
      toast({
        title: "Patient deleted",
        description: `${patient.patientId}: ${result.deletedMeasurements ?? 0} measurements removed. ${result.personDeleted === false ? result.message : ''}`,
        status: result.personDeleted === false ? "warning" : "success",
        duration: 6000,
        isClosable: true,
      });
      onDeleted(patient);
    } catch (error) {
      toast({ title: "Could not delete patient", description: error.message, status: "error", isClosable: true });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog isOpen={!!patient} leastDestructiveRef={cancelRef} onClose={isDeleting ? () => {} : onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete patient</AlertDialogHeader>
          <AlertDialogBody>
            {patient && (
              <VStack align="stretch" spacing={3}>
                <Box p={3} bg={warningBg} borderRadius="md">
                  <Text fontSize="sm">
                    This permanently deletes <b>{patient.patientId}</b> ({groupLabel(patient.publicToken)}) and all of
                    the patient's measurements from the platform. This cannot be undone.
                  </Text>
                </Box>
                <FormControl>
                  <FormLabel fontSize="sm">Type the patient ID to confirm</FormLabel>
                  <Input
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder={patient.patientId}
                    fontFamily="mono"
                  />
                </FormControl>
              </VStack>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isDeleting}>Cancel</Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={remove}
              isLoading={isDeleting}
              loadingText="Deleting"
              isDisabled={!patient || confirmation !== patient.patientId}
            >
              Delete permanently
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
